"use client";

import { useState, type CSSProperties } from "react";
import Image from "next/image";
import { cn } from "@workspace/ui/lib/utils";

import type { Spine } from "@/lib/books/shelf";
import { SPINE_FONTS, type SpineFont } from "./fonts";

export type ShelfBook = {
  id: string;
  title: string;
  series: string | null;
  author: string;
  shelf: "reading" | "read" | "to-read";
  coverUrl: string | null;
  spineColor: string | null;
  accentColor: string | null;
  pages: number | null;
  publishedYear: number | null;
  rating: number | null;
  finishedOn: string | null;
  spineVersion: number | null;
  spineAspect: number | null;
  largeCoverVersion: number | null;
};

// Rounded-spine lighting: a highlight just inside the left edge, falling off
// into shadow at the right where the spine turns towards the boards.
const SPINE_LIGHT =
  "linear-gradient(90deg, rgb(0 0 0 / 0.3) 0%, rgb(255 255 255 / 0.16) 6%, rgb(255 255 255 / 0.06) 20%, transparent 42%, rgb(0 0 0 / 0.05) 72%, rgb(0 0 0 / 0.34) 100%)";

// Light falling from above: the top of the spine catches it, the foot is in
// the shadow of the books around it.
const OVERHEAD_LIGHT =
  "linear-gradient(to bottom, rgb(255 226 180 / 0.12), transparent 35%, rgb(0 0 0 / 0.28))";

// Fine paper grain so flat colour reads as cloth or board.
export const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 .55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;

export function spineFont(spine: Spine): SpineFont {
  return SPINE_FONTS[spine.typeface % SPINE_FONTS.length] ?? SPINE_FONTS[0]!;
}

export function surnameOf(author: string): string {
  return author.split(",")[0]?.trim().split(/\s+/).at(-1) ?? "";
}

/** The spine as a flat face; callers position and rotate it. */
export function SpineFace({
  book,
  spine,
  width,
  height,
  className,
  style,
}: {
  book: ShelfBook;
  spine: Spine;
  width: number;
  height: number;
  className?: string;
  style?: CSSProperties;
}) {
  const font = spineFont(spine);
  const capitals = font.capitals || spine.capitals || !font.advance;
  const surname = surnameOf(book.author);
  const titleSize = fitTitle(book.title, font, capitals, width, height);

  return (
    <span
      className={cn("absolute overflow-hidden rounded-[2px_2px_1px_1px]", className)}
      style={{
        width,
        height,
        background: spine.background,
        color: spine.foreground,
        fontFamily: font.family,
        ...style,
      }}
    >
      <span className="absolute inset-0 opacity-25 mix-blend-multiply" style={{ backgroundImage: GRAIN }} />

      {spine.trim === "bands" && (
        <>
          <Bands top="7%" colour={spine.accent} />
          <Bands top="88%" colour={spine.accent} />
        </>
      )}
      {spine.trim === "head" && (
        <span className="absolute inset-x-0 top-0 h-[15%]" style={{ background: spine.accent }}>
          <span
            className="absolute inset-x-0 -bottom-[5px] h-px"
            style={{ background: spine.accent }}
          />
        </span>
      )}
      {spine.trim === "foot" && (
        <span
          className="absolute inset-x-0 bottom-0 h-[23%]"
          style={{ background: spine.accent, color: spine.accentForeground }}
        />
      )}

      <span className="relative flex h-full flex-col items-center px-0.5 pb-2.5 pt-4">
        <span className="flex min-h-0 flex-1 items-center">
          <span
            className={cn(
              // Truncation clips across the line too, so the line is kept tall
              // enough for faces whose capitals sit high.
              "max-h-full truncate leading-[1.4] [writing-mode:vertical-rl] [text-orientation:sideways]",
              capitals && "uppercase",
            )}
            style={{
              fontSize: titleSize,
              fontWeight: font.weight,
              letterSpacing: capitals ? `${font.tracking}em` : undefined,
              wordSpacing: capitals ? undefined : "0.12em",
            }}
          >
            {book.title}
          </span>
        </span>
        {spine.trim === "plain" && (
          <span
            className="my-2 size-[5px] shrink-0 rotate-45"
            style={{ background: spine.accent }}
          />
        )}
        <span
          className={cn(
            "max-h-[26%] shrink-0 truncate text-[8.5px] font-semibold uppercase leading-[1.4] tracking-[0.2em] opacity-80 [writing-mode:vertical-rl] [text-orientation:sideways]",
            spine.trim === "foot" && "mt-4 opacity-100",
          )}
          style={spine.trim === "foot" ? { color: spine.accentForeground } : undefined}
        >
          {surname}
        </span>
      </span>

      <span className="absolute inset-0" style={{ backgroundImage: SPINE_LIGHT }} />
      <span className="absolute inset-0" style={{ backgroundImage: OVERHEAD_LIGHT }} />
    </span>
  );
}

/**
 * The title's font size: the usual size for the spine's width, reduced as far
 * as a third when that is what it takes to fit the whole title. Titles that
 * still do not fit are truncated.
 */
function fitTitle(title: string, font: SpineFont, capitals: boolean, width: number, height: number) {
  const preferred = (width < 28 ? 10 : width < 38 ? 11.5 : width < 50 ? 13 : 14) * font.scale;
  // Leave room for the padding, the ornament and the author's surname.
  const room = height * 0.66 - 24;
  const advance = (capitals ? font.capitalAdvance + font.tracking : (font.advance ?? font.capitalAdvance));
  const fitted = room / (title.length * advance);
  return Math.max(preferred * 0.66, Math.min(preferred, fitted));
}

function Bands({ top, colour }: { top: string; colour: string }) {
  return (
    <span className="absolute inset-x-0 flex flex-col gap-[2px]" style={{ top }}>
      <span className="h-[1.5px]" style={{ background: colour }} />
      <span className="h-[1.5px]" style={{ background: colour }} />
    </span>
  );
}

/**
 * The front board: the catalogue cover when there is one, otherwise a cover
 * typeset from the book's own spine colours.
 */
export function CoverArt({
  book,
  spine,
  sizes,
  eager = false,
}: {
  book: ShelfBook;
  spine: Spine;
  sizes: string;
  /** Load straight away rather than when scrolled near. */
  eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);

  return (
    <span
      className="absolute inset-0 flex flex-col justify-between p-[9%]"
      style={{ background: spine.background, color: spine.foreground, fontFamily: spineFont(spine).family }}
    >
      <span className="absolute inset-0 opacity-25 mix-blend-multiply" style={{ backgroundImage: GRAIN }} />
      <span className="relative h-[3px] w-1/3" style={{ background: spine.accent }} />
      <span className="relative text-balance text-[0.95em] font-semibold leading-tight">
        {book.title}
      </span>
      <span className="relative text-[0.6em] font-medium uppercase tracking-[0.18em] opacity-80">
        {book.author}
      </span>
      {book.coverUrl && !failed && (
        <Image
          src={book.coverUrl}
          alt=""
          fill
          sizes={sizes}
          loading={eager ? "eager" : "lazy"}
          onError={() => setFailed(true)}
          className="object-cover"
        />
      )}
    </span>
  );
}

/** Shading where the front board meets the spine. */
export function hingeShade(side: "left" | "right"): string {
  const direction = side === "left" ? "90deg" : "270deg";
  return `linear-gradient(${direction}, rgb(0 0 0 / 0.38) 0%, rgb(255 255 255 / 0.22) 2.2%, rgb(0 0 0 / 0.1) 4.5%, transparent 9%)`;
}
